import firebase from 'firebase';
import alertSound from '../../assets/sounds/alertSound1.ogg';
import { BugTracker } from 'Utils/Bugtracker';

export type NotificationAudio = {
  soundUrl: string;
  leadTimeMs: number;
  play: () => Promise<void>;
};

type NotificationSettings = {
  soundUrl: string;
  leadTimeMs: number;
};

const DEFAULT_NOTIFICATION_TIME_MINUTES = 10;
const DEFAULT_NOTIFICATION_SETTINGS: NotificationSettings = {
  soundUrl: alertSound,
  leadTimeMs: DEFAULT_NOTIFICATION_TIME_MINUTES * 60_000
};

const notificationAudioCache = new Map<string, NotificationAudio>();

const hasWindow = typeof window !== 'undefined';

const createAudioController = (soundUrl: string) => {
  if (!hasWindow) {
    return {
      play: async () => {}
    };
  }

  const audio = new Audio(soundUrl);
  audio.preload = 'auto';

  return {
    play: async () => {
      try {
        audio.currentTime = 0;
        await audio.play();
      } catch (error) {
        console.error('Audio Player Error:', error);
      }
    }
  };
};

const fetchNotificationSettings = async (
  userId: string
): Promise<NotificationSettings> => {
  let settings = { ...DEFAULT_NOTIFICATION_SETTINGS };

  try {
    const doc = await firebase
      .firestore()
      .collection('userAccount')
      .doc(userId)
      .get();

    if (!doc.exists) return settings;

    const data = doc.data() as Record<string, any>;

    const configuredSound =
      data?.notification?.fileRoot || DEFAULT_NOTIFICATION_SETTINGS.soundUrl;

    const notificationMinutesRaw = data?.notificationTime;
    const notificationMinutes =
      typeof notificationMinutesRaw === 'number' && notificationMinutesRaw > 0
        ? notificationMinutesRaw
        : DEFAULT_NOTIFICATION_TIME_MINUTES;

    settings = {
      soundUrl: configuredSound,
      leadTimeMs: notificationMinutes * 60_000
    };
  } catch (error) {
    BugTracker.notify(error);
  }

  return settings;
};

export const getNotificationAudio = async (
  userId: number | string
): Promise<NotificationAudio> => {
  const cacheKey = String(userId);
  const cached = notificationAudioCache.get(cacheKey);
  if (cached) return cached;

  const settings = await fetchNotificationSettings(cacheKey);
  const controller = createAudioController(settings.soundUrl);

  const payload: NotificationAudio = {
    ...settings,
    play: controller.play
  };

  notificationAudioCache.set(cacheKey, payload);
  return payload;
};
